import React, { useEffect, useRef, useState } from 'react';
import { SignInButton } from '@clerk/react';
import { clerkAppearance } from '../clerkAppearance';
import { trackPurchase, clearScanIntentId, clearScanIntentToken, clearUnlockIntent } from '@privisy/ui-bridge';
import { getCreditPurchaseStatus, verifyPurchase, getSubscription } from '../utils/api';
import type { BillingSubscriptionResponse } from '../utils/types';
import type { PaymentMode } from '../utils/routes';
import { isSubscriptionStarted, subscriptionSummary } from '../utils/billing';
import { T } from '../designTokens';

type PaymentState = 'confirming' | 'success' | 'timeout' | 'error';

interface PaymentSuccessPageProps {
  sessionId: string;
  /**
   * What the session bought (#1315). 'subscription' confirms against
   * GET /api/billing/subscription instead of the credit ledger, since a
   * recurring-plan checkout grants no credits.
   */
  mode?: PaymentMode;
  /**
   * The report a monitoring checkout was started from (#1776). The webhook
   * unlocks it (#1775), so the confirmed card offers the way back to it.
   */
  reportId?: string | null;
  /**
   * No Clerk session. In 'unlock' mode (a signed-out unlock checkout,
   * 2026-10-27) the webhook gave the report to the account holding the
   * checkout email, so the confirmed card asks the buyer to sign in as it.
   */
  signedOut?: boolean;
  /**
   * `reportUnlocked` tells the report route whether this purchase unlocked it
   * (#1762), so its banner can say so instead of "Credits added".
   */
  onNavigate: (route: string, outcome?: PurchaseOutcome) => void;
}

export interface PurchaseOutcome {
  reportUnlocked: boolean;
}

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30000;
export default function PaymentSuccessPage({ sessionId, mode = 'credits', reportId = null, signedOut = false, onNavigate }: PaymentSuccessPageProps) {
  const isSubscription = mode === 'subscription';
  const isUnlock = mode === 'unlock' && reportId !== null;
  const reportRoute = reportId ? `#/scan/${encodeURIComponent(reportId)}` : null;
  const [state, setState] = useState<PaymentState>('confirming');
  const [isVerifying, setIsVerifying] = useState(false);
  // Credits the server says this purchase granted; undefined when it did not
  // say, in which case the heading makes no count claim.
  const [creditsAdded, setCreditsAdded] = useState<number | undefined>(undefined);
  // What Stripe actually settled this session for, in cents — read off the
  // same confirmed response as creditsAdded, not GET /api/credits' current
  // list price (a promo code or a reprice spanning the checkout makes those
  // two diverge). undefined until confirmed, in which case no conversion fires.
  const [amountCents, setAmountCents] = useState<number | undefined>(undefined);
  // The confirmed subscription (subscription mode only).
  const [subscription, setSubscription] = useState<BillingSubscriptionResponse | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  // Tracks confirmed status in a mutable ref to prevent stale closure races
  // where a pending 30s polling timer fires after success and overwrites state.
  const confirmedRef = useRef(false);
  // Tracks whether the buyer arrived signed out on an unlock confirmation, so
  // signing in on this card navigates immediately without waiting another 2s.
  const signedOutOnConfirmRef = useRef(false);
  // Focus the card on mount for accessibility
  useEffect(() => {
    cardRef.current?.focus();
  }, []);

  // Fire the purchase conversion once the payment is confirmed *and* the
  // settled amount is known. Deferring is safe (trackPurchase dedupes per
  // session in sessionStorage); guessing is not, because a wrong-valued
  // conversion cannot be retracted once sent. If the amount never resolves,
  // the server-side webhook leg still reports the real Stripe amount.
  // A subscription checkout never sets amountCents, so it fires nothing here:
  // the credit-pack conversion would misreport a recurring plan.
  useEffect(() => {
    if (state === 'success' && amountCents !== undefined) {
      trackPurchase(sessionId, amountCents / 100);
    }
  }, [state, amountCents, sessionId]);

  // Redirect to scanner home on success after 2s. A subscription stays put so
  // the customer can read the plan and renewal date it confirms. A report
  // unlock no longer happens here: its checkout returns to the report itself
  // and the webhook unlocks it (#1775, #1776).
  useEffect(() => {
    // A signed-out unlock buyer stays to sign in; a signed-in one goes
    // straight to the report the webhook just unlocked. Once confirmed, the
    // webhook has already claimed the report (claimForPurchase), so drop any
    // persisted claim cookies before sign-in can trigger useClaimScan.
    if (state === 'success' && isUnlock) {
      clearScanIntentId();
      clearScanIntentToken();
      clearUnlockIntent();
      if (!reportRoute) return;
      if (signedOut) {
        signedOutOnConfirmRef.current = true;
        return;
      }
      if (signedOutOnConfirmRef.current) {
        onNavigate(reportRoute, { reportUnlocked: true });
        return;
      }
      const timer = setTimeout(() => onNavigate(reportRoute, { reportUnlocked: true }), 2000);
      return () => clearTimeout(timer);
    }
    if (state === 'success' && !isSubscription) {
      const timer = setTimeout(() => {
        const route = sessionStorage.getItem('privisy_post_purchase_route');
        if (route && /^#\/(scan|report)\//.test(route)) {
          sessionStorage.removeItem('privisy_post_purchase_route');
          onNavigate(route, { reportUnlocked: false });
        } else {
          onNavigate('#/scanner');
        }
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [state, onNavigate, isSubscription, isUnlock, signedOut, reportRoute]);

  // Poll purchase-status every 2s up to 30s
  useEffect(() => {
    if (!sessionId || state !== 'confirming') return;

    let stopped = false;
    const startTime = Date.now();

    const poll = async () => {
      if (stopped || confirmedRef.current) return;
      try {
        if (isSubscription) {
          // This route renders outside the auth gate, so the first polls can
          // run before Clerk has a token: a 401 lands in the catch below and
          // polling simply continues, like a network error.
          //
          // User-scoped, not session-scoped, by design: the page grants
          // nothing, and checkout answers 409 to anyone already holding a
          // billable subscription, so a live one here can only have come
          // from this checkout (or one racing it in another tab).
          const sub = await getSubscription();
          if (stopped || confirmedRef.current) return;
          if (isSubscriptionStarted(sub)) {
            confirmedRef.current = true;
            setSubscription(sub);
            setState('success');
            return;
          }
        } else {
          const result = await getCreditPurchaseStatus(sessionId);
          if (stopped || confirmedRef.current) return;
          if (result.status === 'confirmed') {
            confirmedRef.current = true;
            setCreditsAdded(result.creditsAdded);
            setAmountCents(result.amountCents);
            setState('success');
            return;
          }
        }
      } catch {
        // Network error — keep polling
      }

      if (stopped || confirmedRef.current) return;
      if (Date.now() - startTime >= POLL_TIMEOUT_MS) {
        if (!confirmedRef.current) setState('timeout');
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    };

    setTimeout(poll, POLL_INTERVAL_MS);
    return () => { stopped = true; };
  }, [sessionId, state, isSubscription]);

  const handleVerify = async () => {
    setIsVerifying(true);
    try {
      if (isSubscription) {
        // verify-purchase answers 409 for a subscription session; the
        // subscription endpoint is the source of truth. Not live yet means
        // the webhook is still in flight, so stay on the timeout card.
        const sub = await getSubscription();
        if (isSubscriptionStarted(sub)) {
          confirmedRef.current = true;
          setSubscription(sub);
          setState('success');
        }
        return;
      }
      const result = await verifyPurchase(sessionId);
      if (result.status === 'confirmed') {
        confirmedRef.current = true;
        setCreditsAdded(result.creditsAdded);
        setAmountCents(result.amountCents);
        setState('success');
      } else {
        setState('error');
      }
    } catch {
      setState('error');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: T.gray50,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
    }}>
      <div
        ref={cardRef}
        tabIndex={-1}
        role="main"
        aria-label="Payment status"
        style={{
          backgroundColor: T.white,
          border: `1px solid ${T.gray200}`,
          borderRadius: '12px',
          padding: '40px 32px',
          width: '100%',
          maxWidth: '480px',
        }}
      >
        <div aria-live="polite" aria-atomic="true" style={{ textAlign: 'center' }}>
          {state === 'confirming' && <ConfirmingState />}
          {state === 'success' && isUnlock && reportRoute && (
            <UnlockSuccessState onNavigate={onNavigate} reportRoute={reportRoute} signedOut={signedOut} />
          )}
          {state === 'success' && !isUnlock && (isSubscription && subscription
            ? <SubscriptionSuccessState onNavigate={onNavigate} subscription={subscription} reportId={reportId} />
            : <SuccessState onNavigate={onNavigate} creditsAdded={creditsAdded} />)}
          {state === 'timeout' && (
            <TimeoutState
              sessionId={sessionId}
              onVerify={handleVerify}
              isVerifying={isVerifying}
              isSubscription={isSubscription}
            />
          )}
          {state === 'error' && <ErrorState sessionId={sessionId} />}
        </div>
      </div>
    </div>
  );
}

function ConfirmingState() {
  return (
    <>
      <div style={{ marginBottom: '20px' }}>
        <svg
          width="40" height="40"
          viewBox="0 0 40 40"
          style={{ animation: 'spin 1s linear infinite' }}
          aria-hidden="true"
        >
          <circle cx="20" cy="20" r="16" fill="none" stroke={T.gray200} strokeWidth="4" />
          <path d="M20 4 a16 16 0 0 1 16 16" fill="none" stroke={T.cobalt600} strokeWidth="4" strokeLinecap="round" />
        </svg>
      </div>
      <p style={{
        margin: 0,
        fontSize: '16px',
        color: T.gray700,
        fontFamily: T.fontBody,
        fontWeight: 400,
      }}>
        Confirming your payment...
      </p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}

function creditsAddedHeading(creditsAdded: number | undefined): string {
  if (creditsAdded === undefined || !Number.isFinite(creditsAdded)) return 'Credits added!';
  return `${creditsAdded} ${creditsAdded === 1 ? 'credit' : 'credits'} added!`;
}

function SuccessState({
  onNavigate,
  creditsAdded,
}: {
  onNavigate: (route: string, outcome?: PurchaseOutcome) => void;
  creditsAdded: number | undefined;
}) {
  const postPurchaseRoute = sessionStorage.getItem('privisy_post_purchase_route');
  const isValidRoute = postPurchaseRoute && /^#\/(scan|report)\//.test(postPurchaseRoute);
  
  const buttonLabel = isValidRoute ? 'Back to your report' : 'Start scanning';
  const handleButtonClick = () => {
    if (isValidRoute) {
      sessionStorage.removeItem('privisy_post_purchase_route');
      onNavigate(postPurchaseRoute, { reportUnlocked: false });
    } else {
      onNavigate('#/scanner');
    }
  };

  return (
    <>
      <div style={{ marginBottom: '16px' }}>
        <svg width="48" height="48" viewBox="0 0 48 48" aria-hidden="true">
          <circle cx="24" cy="24" r="24" fill={T.successBg} />
          <path d="M14 24l7 7 13-14" stroke={T.success} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      </div>
      <h2 style={{
        margin: '0 0 8px',
        fontSize: '20px',
        fontWeight: 600,
        color: T.gray900,
        fontFamily: T.fontDisplay,
      }}>
        {creditsAddedHeading(creditsAdded)}
      </h2>
      <p style={{ margin: '0 0 20px', fontSize: '14px', color: T.gray500, fontFamily: T.fontBody }}>
        {isValidRoute ? 'Redirecting you back to your report...' : 'Your scan credits are ready. Redirecting to scanner...'}
      </p>
      <button
        onClick={handleButtonClick}
        style={{
          padding: '10px 24px',
          fontSize: '14px',
          fontWeight: 600,
          color: T.white,
          backgroundColor: T.cobalt600,
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontFamily: T.fontBody,
          minHeight: '44px',
        }}
        aria-label={buttonLabel}
      >
        {buttonLabel}
      </button>
    </>
  );
}

/**
 * A signed-out unlock purchase is confirmed (2026-10-27). The report now
 * belongs to the account holding the checkout email, created by the webhook
 * when there was none, so the buyer signs in with that email to open it.
 */
function UnlockSuccessState({
  onNavigate,
  reportRoute,
  signedOut,
}: {
  onNavigate: (route: string, outcome?: PurchaseOutcome) => void;
  reportRoute: string;
  signedOut: boolean;
}) {
  const buttonStyle: React.CSSProperties = {
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: 600,
    color: T.white,
    backgroundColor: T.cobalt600,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontFamily: T.fontBody,
    minHeight: '44px',
  };
  return (
    <>
      <div style={{ marginBottom: '16px' }}>
        <svg width="48" height="48" viewBox="0 0 48 48" aria-hidden="true">
          <circle cx="24" cy="24" r="24" fill={T.successBg} />
          <path d="M14 24l7 7 13-14" stroke={T.success} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      </div>
      <h2 style={{
        margin: '0 0 8px',
        fontSize: '20px',
        fontWeight: 600,
        color: T.gray900,
        fontFamily: T.fontDisplay,
      }}>
        Your report is unlocked
      </h2>
      <p style={{ margin: '0 0 20px', fontSize: '14px', color: T.gray500, lineHeight: '1.6', fontFamily: T.fontBody }}>
        {signedOut
          ? 'It is saved to an account for the email you paid with. Sign in with that email to open it.'
          : 'Taking you to your report...'}
      </p>
      {signedOut ? (
        <SignInButton
          mode="modal"
          forceRedirectUrl={`${window.location.pathname}${window.location.search}${reportRoute}`}
          appearance={clerkAppearance}
        >
          <button type="button" style={buttonStyle} data-testid="unlock-success-sign-in">
            Sign in to open your report
          </button>
        </SignInButton>
      ) : (
        <button type="button" style={buttonStyle} onClick={() => onNavigate(reportRoute, { reportUnlocked: true })}>
          Open your report
        </button>
      )}
    </>
  );
}

function SubscriptionSuccessState({
  onNavigate,
  subscription,
  reportId,
}: {
  onNavigate: (route: string) => void;
  subscription: BillingSubscriptionResponse;
  reportId: string | null;
}) {
  // Started from a locked report: monitoring unlocks it (#1775), so the way
  // forward is back to that report rather than to billing.
  const reportRoute = reportId ? `#/scan/${encodeURIComponent(reportId)}` : null;
  return (
    <>
      <div style={{ marginBottom: '16px' }}>
        <svg width="48" height="48" viewBox="0 0 48 48" aria-hidden="true">
          <circle cx="24" cy="24" r="24" fill={T.successBg} />
          <path d="M14 24l7 7 13-14" stroke={T.success} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        </svg>
      </div>
      <h2 style={{
        margin: '0 0 8px',
        fontSize: '20px',
        fontWeight: 600,
        color: T.gray900,
        fontFamily: T.fontDisplay,
      }}>
        Continuous monitoring is active
      </h2>
      <p style={{ margin: '0 0 20px', fontSize: '14px', color: T.gray500, fontFamily: T.fontBody }}>
        {subscriptionSummary(subscription)}
      </p>
      <button
        onClick={() => onNavigate(reportRoute ?? '#/billing')}
        style={{
          padding: '10px 24px',
          fontSize: '14px',
          fontWeight: 600,
          color: T.white,
          backgroundColor: T.cobalt600,
          border: 'none',
          borderRadius: '8px',
          cursor: 'pointer',
          fontFamily: T.fontBody,
          minHeight: '44px',
        }}
      >
        {reportRoute ? 'Back to your report' : 'Go to billing'}
      </button>
    </>
  );
}

function TimeoutState({
  sessionId,
  onVerify,
  isVerifying,
  isSubscription,
}: {
  sessionId: string;
  onVerify: () => void;
  isVerifying: boolean;
  isSubscription: boolean;
}) {
  return (
    <>
      <div style={{ marginBottom: '16px' }}>
        <svg width="48" height="48" viewBox="0 0 48 48" aria-hidden="true">
          <circle cx="24" cy="24" r="24" fill={T.warningBg} />
          <path d="M24 16v9" stroke={T.warning} strokeWidth="3" strokeLinecap="round" />
          <circle cx="24" cy="31" r="1.5" fill={T.warning} />
        </svg>
      </div>
      <h2 style={{
        margin: '0 0 8px',
        fontSize: '18px',
        fontWeight: 600,
        color: T.gray900,
        fontFamily: T.fontBody,
      }}>
        Payment received
      </h2>
      <p style={{ margin: '0 0 24px', fontSize: '14px', color: T.gray500, lineHeight: '1.6', fontFamily: T.fontBody }}>
        {isSubscription
          ? 'Your payment was received. Your subscription is being activated — this usually takes just a moment.'
          : 'Your payment was received. Your report is being prepared — this usually takes just a moment.'}
      </p>
      <button
        onClick={onVerify}
        disabled={isVerifying}
        style={{
          padding: '12px 24px',
          fontSize: '14px',
          fontWeight: 600,
          color: T.white,
          backgroundColor: isVerifying ? T.cobalt500 : T.cobalt600,
          border: 'none',
          borderRadius: '8px',
          cursor: isVerifying ? 'not-allowed' : 'pointer',
          fontFamily: T.fontBody,
          minHeight: '44px',
          opacity: isVerifying ? 0.75 : 1,
        }}
        aria-label="Verify your purchase directly with Stripe"
      >
        {isVerifying ? 'Verifying...' : 'Verify Purchase'}
      </button>
      <p style={{ margin: '16px 0 0', fontSize: '12px', color: T.gray400, fontFamily: T.fontBody }}>
        Session ID: <span style={{ fontFamily: T.fontMono, userSelect: 'all' }}>{sessionId}</span>
      </p>
    </>
  );
}

function ErrorState({ sessionId }: { sessionId: string }) {
  return (
    <>
      <div style={{ marginBottom: '16px' }}>
        <svg width="48" height="48" viewBox="0 0 48 48" aria-hidden="true">
          <circle cx="24" cy="24" r="24" fill={T.errorBg} />
          <path d="M17 17l14 14M31 17l-14 14" stroke={T.error} strokeWidth="3" strokeLinecap="round" />
        </svg>
      </div>
      <h2 style={{
        margin: '0 0 8px',
        fontSize: '18px',
        fontWeight: 600,
        color: T.gray900,
        fontFamily: T.fontBody,
      }}>
        Something went wrong
      </h2>
      <p style={{ margin: '0 0 20px', fontSize: '14px', color: T.gray500, lineHeight: '1.6', fontFamily: T.fontBody }}>
        Your payment is safe. Please contact{' '}
        <a href="mailto:support@privisy.com" style={{ color: T.cobalt600 }}>support@privisy.com</a>{' '}
        with your session ID:
      </p>
      <p style={{
        margin: '0',
        padding: '8px 12px',
        backgroundColor: T.gray50,
        border: `1px solid ${T.gray200}`,
        borderRadius: '6px',
        fontFamily: T.fontMono,
        fontSize: '12px',
        color: T.gray700,
        userSelect: 'all',
        wordBreak: 'break-all',
      }}>
        {sessionId}
      </p>
    </>
  );
}
